/**
 * ConnectionRules — enforces DMN 1.3 connection rules on the LogicFlow canvas.
 *
 * On 'edge:add' it looks up (sourceType, targetType) in the rule matrix:
 *  - If the combo is invalid → delete the edge and show a brief warning toast.
 *  - If the combo is valid but the edge type is wrong (default was used) →
 *    replace the edge with the correct type.
 *
 * Also exposes:
 *  resolveEdgeType(srcType, tgtType) → edgeType|null  (used by ContextPad)
 *  isValidConnection(srcType, tgtType) → bool          (used for visual hints)
 */

// ─── DMN 1.3 connection matrix ────────────────────────────────────────────────
// Rows = source type, columns = target type, value = edge type to use.
// null means the connection is forbidden.

const RULES = {
  'dmn:InputData': {
    'dmn:Decision':               'dmn:InformationRequirement',
    'dmn:KnowledgeSource':        'dmn:AuthorityRequirement',
  },
  'dmn:Decision': {
    'dmn:Decision':               'dmn:InformationRequirement',
    'dmn:KnowledgeSource':        'dmn:AuthorityRequirement',
  },
  'dmn:BusinessKnowledgeModel': {
    'dmn:Decision':               'dmn:KnowledgeRequirement',
    'dmn:BusinessKnowledgeModel': 'dmn:KnowledgeRequirement',
  },
  'dmn:KnowledgeSource': {
    'dmn:Decision':               'dmn:AuthorityRequirement',
    'dmn:BusinessKnowledgeModel': 'dmn:AuthorityRequirement',
    'dmn:KnowledgeSource':        'dmn:AuthorityRequirement',
  },
};

// TextAnnotation connects via Association to anything
const ANNOTATION_TYPES = new Set(['dmn:TextAnnotation']);

// ─── Warning toast ────────────────────────────────────────────────────────────

let _toastTimeout = null;

function showWarning(msg) {
  let el = document.getElementById('epx-dmn-conn-toast');
  if (!el) {
    el = document.createElement('div');
    el.id = 'epx-dmn-conn-toast';
    el.style.cssText = `
      position:fixed; bottom:24px; left:50%; transform:translateX(-50%) translateY(0);
      background:#fffbe6; border:1px solid #ffe58f; color:#614700;
      padding:8px 18px; border-radius:6px; font-size:12.5px;
      box-shadow:0 4px 12px rgba(0,0,0,.12); z-index:99999;
      transition:opacity .25s; pointer-events:none; white-space:nowrap;
    `;
    document.body.appendChild(el);
  }
  el.textContent = msg;
  el.style.opacity = '1';
  clearTimeout(_toastTimeout);
  _toastTimeout = setTimeout(() => { el.style.opacity = '0'; }, 2800);
}

// ─── Main class ───────────────────────────────────────────────────────────────

export class ConnectionRules {
  constructor(lf) {
    this._lf = lf;
    this._bind();
  }

  /**
   * Look up the allowed edge type for a (source, target) combo.
   * Returns the edge type string, or null if the connection is forbidden.
   */
  resolveEdgeType(srcType, tgtType) {
    if (ANNOTATION_TYPES.has(srcType) || ANNOTATION_TYPES.has(tgtType)) {
      return 'dmn:Association';
    }
    return (RULES[srcType] && RULES[srcType][tgtType]) || null;
  }

  isValidConnection(srcType, tgtType) {
    return this.resolveEdgeType(srcType, tgtType) !== null;
  }

  /**
   * Returns an array of target types that are valid for the given source type.
   * Useful for filtering append-panel items.
   */
  validTargetsFor(srcType) {
    if (ANNOTATION_TYPES.has(srcType)) return [];
    const row = RULES[srcType] || {};
    return Object.keys(row);
  }

  _bind() {
    this._lf.on('edge:add', ({ data }) => this._onEdgeAdded(data));
  }

  _onEdgeAdded(edge) {
    if (!edge || !edge.sourceNodeId || !edge.targetNodeId) return;

    const { nodes } = this._lf.getGraphData();
    const nodeMap = new Map((nodes || []).map((n) => [n.id, n]));
    const src = nodeMap.get(edge.sourceNodeId);
    const tgt = nodeMap.get(edge.targetNodeId);
    if (!src || !tgt) return;

    // Self-connections are not valid in DMN
    if (edge.sourceNodeId === edge.targetNodeId) {
      this._deleteEdge(edge.id);
      showWarning('Los auto-loops no están permitidos en DMN.');
      return;
    }

    const correct = this.resolveEdgeType(src.type, tgt.type);

    if (!correct) {
      this._deleteEdge(edge.id);
      showWarning(`Conexión no válida según DMN 1.3: ${_label(src.type)} → ${_label(tgt.type)}`);
      return;
    }

    // If LogicFlow placed the wrong default edge type, replace it
    if (edge.type !== correct) {
      // Schedule replacement after this event cycle so LogicFlow finishes its own processing
      setTimeout(() => this._replaceEdgeType(edge, src, tgt, correct), 0);
    }
  }

  _replaceEdgeType(edge, src, tgt, correctType) {
    try { this._lf.deleteEdge(edge.id); } catch (_) { return; }
    try {
      this._lf.addEdge({
        id: edge.id,
        type: correctType,
        sourceNodeId: edge.sourceNodeId,
        targetNodeId: edge.targetNodeId,
        startPoint: edge.startPoint,
        endPoint: edge.endPoint,
        pointsList: edge.pointsList && edge.pointsList.length ? edge.pointsList : undefined,
      });
      const em = this._lf.getEdgeModelById(edge.id);
      if (em) {
        if (typeof em.setAnchors === 'function') em.setAnchors();
        if (typeof em.initPoints === 'function') em.initPoints();
      }
    } catch (_) {}
  }

  _deleteEdge(id) {
    try { this._lf.deleteEdge(id); } catch (_) {}
  }
}

// Friendly labels for toast messages
function _label(type) {
  const labels = {
    'dmn:Decision':               'Decision',
    'dmn:InputData':              'Input Data',
    'dmn:BusinessKnowledgeModel': 'BKM',
    'dmn:KnowledgeSource':        'Knowledge Source',
    'dmn:TextAnnotation':         'Text Annotation',
  };
  return labels[type] || type;
}
